/**
 * PopupInfo Web Component
 * An informational popup that appears on hover or click
 * 
 * Usage:
 *   <script src="popup-info.js"></script>
 *   
 *   <!-- Basic usage with default question mark icon -->
 *   <popup-info>
 *     This is the popup content. It can contain <strong>HTML</strong>.
 *   </popup-info>
 *   
 *   <!-- With custom icon (Font Awesome) -->
 *   <popup-info icon="fa-solid fa-circle-info">
 *     Information about this feature.
 *   </popup-info>
 *   
 *   <!-- With custom image -->
 *   <popup-info image="icon.png" image-width="20px">
 *     Content appears when hovering the image.
 *   </popup-info>
 *   
 *   <!-- With emoji icon -->
 *   <popup-info emoji="💡">
 *     A helpful tip!
 *   </popup-info>
 *   
 *   <!-- Pinnable (stays open on click) -->
 *   <popup-info pinnable>
 *     Click the icon to pin this popup open.
 *   </popup-info>
 *   
 *   <!-- Different positions -->
 *   <popup-info position="top">Content above</popup-info>
 *   <popup-info position="left">Content to the left</popup-info>
 *   <popup-info position="bottom">Content below</popup-info>
 * 
 * Attributes:
 *   icon         - Font Awesome class(es) for the icon
 *   image        - URL of a custom image to use as the icon
 *   image-width  - Width of the custom image (default: 1rem)
 *   image-height - Height of the custom image
 *   emoji        - Emoji character to use as icon
 *   position     - Popup position: "right" (default), "left", "top", "bottom"
 *   pinnable     - If present, popup can be pinned open by clicking
 *   trigger      - "hover" (default), "click", or "both"
 *   max-width    - Maximum width of the popup (default: 300px)
 * 
 * Events:
 *   popup-open   - Fired when popup opens
 *   popup-close  - Fired when popup closes
 *   popup-pin    - Fired when popup is pinned. Detail: { pinned: boolean }
 * 
 * Properties:
 *   pinned       - Get/set the pinned state
 *   open()       - Open the popup programmatically
 *   close()      - Close the popup
 *   toggle()     - Toggle the popup
 * 
 * CSS Custom Properties:
 *   --popup-bg              - Popup background (default: #fff)
 *   --popup-border-color    - Popup border color (default: #d4d0c8)
 *   --popup-shadow          - Popup box-shadow
 *   --popup-text-color      - Text color (default: #2c2c2c)
 *   --popup-icon-color      - Icon color (default: #8b7355)
 *   --popup-icon-hover      - Icon hover color (default: #6d5a43)
 */

(function() {
  'use strict';

  // Default question mark SVG icon
  const DEFAULT_ICON_SVG = `
    <svg viewBox="0 0 29.536 29.536" class="default-icon">
      <path d="M14.768,0C6.611,0,0,6.609,0,14.768c0,8.155,6.611,14.767,14.768,14.767s14.768-6.612,14.768-14.767
        C29.535,6.609,22.924,0,14.768,0z M14.768,27.126c-6.828,0-12.361-5.532-12.361-12.359c0-6.828,5.533-12.362,12.361-12.362
        c6.826,0,12.359,5.535,12.359,12.362C27.127,21.594,21.594,27.126,14.768,27.126z"/>
      <path d="M14.385,19.337c-1.338,0-2.289,0.951-2.289,2.34c0,1.336,0.926,2.339,2.289,2.339c1.414,0,2.314-1.003,2.314-2.339
        C16.672,20.288,15.771,19.337,14.385,19.337z"/>
      <path d="M14.742,6.092c-1.824,0-3.34,0.513-4.293,1.053l0.875,2.804c0.668-0.462,1.697-0.772,2.545-0.772
        c1.285,0.027,1.879,0.644,1.879,1.543c0,0.85-0.67,1.697-1.494,2.701c-1.156,1.364-1.594,2.701-1.516,4.012l0.025,0.669h3.42
        v-0.463c-0.025-1.158,0.387-2.162,1.311-3.215c0.979-1.08,2.211-2.366,2.211-4.321C19.705,7.968,18.139,6.092,14.742,6.092z"/>
    </svg>
  `;

  const STYLES = `
    @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&display=swap');
    
    :host {
      display: inline-block;
      position: relative;
      font-family: 'Crimson Pro', Georgia, serif;
      font-size: 1rem;
      color: #2c2c2c;
      vertical-align: middle;
    }
    
    .wrapper {
      display: inline-flex;
      align-items: center;
      position: relative;
    }
    
    .icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--popup-icon-color, #8b7355);
      transition: color 0.15s, transform 0.15s;
    }
    
    .icon:hover {
      color: var(--popup-icon-hover, #6d5a43);
    }
    
    .icon.pinned {
      color: var(--popup-icon-hover, #6d5a43);
      transform: scale(1.1);
    }
    
    .default-icon {
      width: 1rem;
      height: 1rem;
      fill: currentColor;
    }
    
    .icon img {
      display: block;
    }
    
    .icon .emoji {
      font-size: 1.1em;
      line-height: 1;
    }
    
    .popup {
      position: absolute;
      z-index: 1000;
      min-width: 150px;
      max-width: var(--popup-max-width, 300px);
      padding: 0.75rem 1rem;
      background: var(--popup-bg, #fff);
      border: 1px solid var(--popup-border-color, #d4d0c8);
      box-shadow: var(--popup-shadow, 0 4px 12px rgba(0, 0, 0, 0.1));
      color: var(--popup-text-color, #2c2c2c);
      font-size: 0.9rem;
      line-height: 1.5;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease;
      transform: translateY(4px);
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    .popup-content {
      /* Container for slotted content to ensure proper containment */
      max-height: 400px;
      overflow-y: auto;
      overflow-x: hidden;
    }
    
    .popup.visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
    
    /* Position: right (default) */
    .popup.position-right {
      left: calc(100% + 8px);
      top: 50%;
      transform: translateY(-50%) translateX(4px);
    }
    
    .popup.position-right.visible {
      transform: translateY(-50%) translateX(0);
    }
    
    /* Position: left */
    .popup.position-left {
      right: calc(100% + 8px);
      top: 50%;
      transform: translateY(-50%) translateX(-4px);
    }
    
    .popup.position-left.visible {
      transform: translateY(-50%) translateX(0);
    }
    
    /* Position: top */
    .popup.position-top {
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%) translateY(-4px);
    }
    
    .popup.position-top.visible {
      transform: translateX(-50%) translateY(0);
    }
    
    /* Position: bottom */
    .popup.position-bottom {
      top: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%) translateY(4px);
    }
    
    .popup.position-bottom.visible {
      transform: translateX(-50%) translateY(0);
    }
    
    /* Arrow indicators */
    .popup::before {
      content: '';
      position: absolute;
      width: 8px;
      height: 8px;
      background: var(--popup-bg, #fff);
      border: 1px solid var(--popup-border-color, #d4d0c8);
      transform: rotate(45deg);
    }
    
    .popup.position-right::before {
      left: -5px;
      top: 50%;
      margin-top: -5px;
      border-right: none;
      border-top: none;
    }
    
    .popup.position-left::before {
      right: -5px;
      top: 50%;
      margin-top: -5px;
      border-left: none;
      border-bottom: none;
    }
    
    .popup.position-top::before {
      bottom: -5px;
      left: 50%;
      margin-left: -5px;
      border-left: none;
      border-top: none;
    }
    
    .popup.position-bottom::before {
      top: -5px;
      left: 50%;
      margin-left: -5px;
      border-right: none;
      border-bottom: none;
    }
    
    /* Popup content styling */
    .popup ::slotted(*) {
      /* Ensure all slotted content respects the container */
      max-width: 100%;
      box-sizing: border-box;
    }
    
    .popup ::slotted(p) {
      margin: 0 0 0.5rem 0;
    }
    
    .popup ::slotted(p:last-child) {
      margin-bottom: 0;
    }
    
    .popup ::slotted(a) {
      color: #6d5a43;
    }
    
    .popup ::slotted(code) {
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.85em;
      background: #f5f4f2;
      padding: 0.1rem 0.3rem;
      border: 1px solid #e8e5e0;
    }
    
    .popup ::slotted(ul),
    .popup ::slotted(ol) {
      margin: 0.5rem 0;
      padding-left: 1.25rem;
    }
    
    .popup ::slotted(li) {
      margin: 0.25rem 0;
    }
    
    /* Pin indicator */
    .pin-indicator {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 6px;
      height: 6px;
      background: #8b7355;
      border-radius: 50%;
      opacity: 0;
      transition: opacity 0.15s;
    }
    
    .popup.pinned .pin-indicator {
      opacity: 1;
    }
    
    /* Touch device adjustments */
    @media (hover: none) {
      .icon:hover {
        color: var(--popup-icon-color, #8b7355);
      }
      
      .icon:active {
        color: var(--popup-icon-hover, #6d5a43);
      }
    }
  `;

  class PopupInfo extends HTMLElement {
    static get observedAttributes() {
      return [
        'icon', 'image', 'image-width', 'image-height', 'emoji',
        'position', 'pinnable', 'trigger', 'max-width'
      ];
    }

    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this._pinned = false;
      this._isOpen = false;
    }

    connectedCallback() {
      this.render();
      this._setupListeners();
    }

    disconnectedCallback() {
      // Clean up document-level listeners
      document.removeEventListener('click', this._documentClickHandler);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      // Re-render for visual changes
      if (['icon', 'image', 'image-width', 'image-height', 'emoji', 'position', 'max-width'].includes(name)) {
        this.render();
        this._setupListeners();
      }
    }

    // === Attribute getters ===

    get iconClass() {
      return this.getAttribute('icon') || '';
    }

    get imageSrc() {
      return this.getAttribute('image') || '';
    }

    get imageWidth() {
      return this.getAttribute('image-width') || '1rem';
    }

    get imageHeight() {
      return this.getAttribute('image-height') || '';
    }

    get emoji() {
      return this.getAttribute('emoji') || '';
    }

    get position() {
      return this.getAttribute('position') || 'right';
    }

    get isPinnable() {
      return this.hasAttribute('pinnable');
    }

    get trigger() {
      return this.getAttribute('trigger') || 'hover';
    }

    get maxWidth() {
      return this.getAttribute('max-width') || '300px';
    }

    // === Public API ===

    /**
     * Get/set the pinned state
     */
    get pinned() {
      return this._pinned;
    }

    set pinned(value) {
      this._pinned = !!value;
      this._updatePinnedState();
    }

    /**
     * Open the popup
     */
    open() {
      this._isOpen = true;
      const popup = this.shadowRoot.querySelector('.popup');
      if (popup) {
        popup.classList.add('visible');
        this.dispatchEvent(new CustomEvent('popup-open'));
      }
    }

    /**
     * Close the popup
     */
    close() {
      if (this._pinned) return;
      
      this._isOpen = false;
      const popup = this.shadowRoot.querySelector('.popup');
      if (popup) {
        popup.classList.remove('visible');
        this.dispatchEvent(new CustomEvent('popup-close'));
      }
    }

    /**
     * Toggle the popup
     */
    toggle() {
      if (this._isOpen) {
        if (this._pinned) {
          this._pinned = false;
          this._updatePinnedState();
          this.close();
        } else {
          this.close();
        }
      } else {
        this.open();
      }
    }

    // === End Public API ===

    _updatePinnedState() {
      const icon = this.shadowRoot.querySelector('.icon');
      const popup = this.shadowRoot.querySelector('.popup');
      
      if (icon) {
        icon.classList.toggle('pinned', this._pinned);
      }
      if (popup) {
        popup.classList.toggle('pinned', this._pinned);
      }
      
      this.dispatchEvent(new CustomEvent('popup-pin', {
        detail: { pinned: this._pinned }
      }));
    }

    _setupListeners() {
      const icon = this.shadowRoot.querySelector('.icon');
      const popup = this.shadowRoot.querySelector('.popup');
      
      if (!icon || !popup) return;

      const trigger = this.trigger;

      // Hover events
      if (trigger === 'hover' || trigger === 'both') {
        icon.addEventListener('mouseenter', () => this.open());
        icon.addEventListener('mouseleave', () => {
          if (!this._pinned) this.close();
        });
        
        popup.addEventListener('mouseenter', () => this.open());
        popup.addEventListener('mouseleave', () => {
          if (!this._pinned) this.close();
        });
      }

      // Click events
      if (trigger === 'click' || trigger === 'both' || this.isPinnable) {
        icon.addEventListener('click', (e) => {
          e.stopPropagation();
          
          if (this.isPinnable && this._isOpen) {
            this._pinned = !this._pinned;
            this._updatePinnedState();
            if (!this._pinned && trigger === 'click') {
              this.close();
            }
          } else if (trigger === 'click') {
            this.toggle();
          }
        });
      }

      // Close on outside click when pinnable
      this._documentClickHandler = (e) => {
        if (this._pinned && !this.contains(e.target)) {
          this._pinned = false;
          this._updatePinnedState();
          if (this.trigger === 'click') {
            this.close();
          }
        }
      };
      
      document.addEventListener('click', this._documentClickHandler);
    }

    _renderIcon() {
      if (this.emoji) {
        return `<span class="emoji">${this._escapeHtml(this.emoji)}</span>`;
      }
      
      if (this.imageSrc) {
        const widthStyle = `width: ${this.imageWidth};`;
        const heightStyle = this.imageHeight ? `height: ${this.imageHeight};` : '';
        return `<img src="${this._escapeHtml(this.imageSrc)}" style="${widthStyle}${heightStyle}" alt="Info">`;
      }
      
      if (this.iconClass) {
        return `<i class="${this._escapeHtml(this.iconClass)}"></i>`;
      }
      
      return DEFAULT_ICON_SVG;
    }

    _escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    render() {
      const positionClass = `position-${this.position}`;
      
      this.shadowRoot.innerHTML = `
        <style>${STYLES}</style>
        
        <span class="wrapper">
          <span class="icon" role="button" aria-label="Show information" tabindex="0">
            ${this._renderIcon()}
          </span>
          
          <div class="popup ${positionClass}" 
               role="tooltip"
               style="--popup-max-width: ${this.maxWidth};">
            ${this.isPinnable ? '<span class="pin-indicator"></span>' : ''}
            <div class="popup-content">
              <slot></slot>
            </div>
          </div>
        </span>
      `;

      // Keyboard accessibility
      const icon = this.shadowRoot.querySelector('.icon');
      icon.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.isPinnable && this._isOpen) {
            this._pinned = !this._pinned;
            this._updatePinnedState();
          } else {
            this.toggle();
          }
        }
        if (e.key === 'Escape' && this._isOpen) {
          this._pinned = false;
          this._updatePinnedState();
          this.close();
        }
      });
    }
  }

  customElements.define('popup-info', PopupInfo);

  // Export for module usage
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { PopupInfo };
  }
})();
